import React from 'react'
import { connectHits } from 'react-instantsearch-dom'
import PropTypes from 'prop-types'
import { camelCase } from 'changecase-objects'
import { Price, Rating, Grid, Box } from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import { helpers } from '../../../helpers'
import { ResultFallback, Stats, ClearFilters, trackAlgoliaEvent, CurrentRefinements } from './'
import { Analytics } from '../../../dbcom'
import { PlusLogo } from '../icons'

/**
 * Displays hits for search results
 *
 * @param {object} hit - A result returned from search.
 * @param {boolean} isRoot - whether the hit is part of a search result
 *
 * @returns {function} React Component
 */
const Hit = ({ hit, isRoot = false }) => {
  // The camelCase function removes these params so we declare them here
  const { __queryID = '', __position = 0 } = hit

  const {
    objectId,
    name = '',
    avgRating = 0,
    reviewsCount = 0,
    slug = '',
    subscribable = {},
    onSale = false,
    producers = [],
    variants = [],
    image = {},
    price = 0,
  } = camelCase(hit || {})

  const variant =
    variants.filter((productVariant) => productVariant.isMaster)[0] || variants[0] || {}

  // Grabs a list of available prices available.
  const allPrices = variants.map(({ salePrice, msrp }) => (onSale ? salePrice : msrp))

  // Filters out 0 values, duplicates and sorts from least to greatest
  const prices = [...new Set(allPrices.filter(Boolean))].sort((a, b) => a - b)

  /**
   * Returns the link to the product. Returns the empty string if the product
   * doesn't have a slug.
   *
   * @returns {string} the product url
   */
  const getProductLink = () => {
    if (!slug) {
      return ''
    }

    return `/p/${slug}`
  }

  /**
   * On click event handler for when a user clicks the product card. Tracks
   * the product as having been clicked and then takes the user to the product's
   * destination.
   */
  const onClickHandler = () => {
    // Tracks click event on google analytics
    Analytics.track(
      'product search click',
      {
        id: objectId,
        name,
        vendor: variant.vendor,
        brand: producers,
        category: variant.department,
        variant,
        price,
      },
      variant.name,
    )

    let insightName = 'clickedObjectIDs'
    let eventName = 'Product Clicked'

    const algoliaTrackingParams = {
      objectIDs: [objectId],
      positions: [__position],
    }

    // Include the query id if the hit is search related
    if (__queryID && isRoot) {
      algoliaTrackingParams.queryID = __queryID
      insightName = 'clickedObjectIDsAfterSearch'
      eventName = 'Search Result Clicked'
    }

    // Track event with algolia
    trackAlgoliaEvent(insightName, { eventName, ...algoliaTrackingParams })
  }

  /**
   * Renders a placeholder of a detailed product card.
   *
   * @returns {string} Placeholder JSX
   */
  const renderPlaceholder = () => {
    return (
      <div className="detailed-product-card is-placeholder">
        <figure />
        <div className="product-card-content">
          <div className="price" />
          <div className="product-details">
            <div className="product-name" />
            <div className="producer" />
          </div>
          <div className="details">
            <Rating average="3.5" />
          </div>
        </div>
      </div>
    )
  }

  /**
   * Renders a special callout message for the product.
   *
   * @returns {string} Placeholder JSX
   */
  const renderCallout = () => {
    let calloutText
    let calloutClass

    if (onSale) {
      calloutClass = 'is-sale'
      calloutText = 'On Sale Now'
    }

    return <div className={`card-callout ${calloutClass}`}>{calloutText}</div>
  }

  /**
   * Method to replace images that have failed to load.
   *
   * @param {object} event - the element object
   */
  const handleImageError = (event) => {
    const fallbackText = document.createElement('span')

    fallbackText.className = 'product-fallback has-drop-shadow'
    fallbackText.appendChild(document.createTextNode(image.alt || name || ''))
    event.target.replaceWith(fallbackText)
  }

  if (!hit) {
    return renderPlaceholder()
  }
  const classes = ['detailed-product-card', 'is-loaded']

  return (
    <div className="card-wrapper">
      <a
        href={getProductLink()}
        aria-label={name}
        className={classes.join(' ')}
        onClick={onClickHandler}
      >
        {renderCallout()}
        <figure data-hover-text="Learn More" className="can-override-before-background">
          <img
            data-original={image.url || ''}
            src={image.url}
            data-fallback-text={name || ''}
            alt={image.alt || name || ''}
            className="is-lazy fade-in has-drop-shadow"
            onError={handleImageError}
          />
        </figure>
        <div className="product-card-content">
          <div className="price">
            {prices.length <= 1 ? (
              <Price value={prices[0]} />
            ) : (
              <Price.Range min={prices[0]} max={prices[prices.length - 1]} />
            )}
          </div>
          <div className="product-details">
            <div className="product-name">{name}</div>
            <div className="producer">{helpers.formatProducers(producers)}</div>
          </div>
          <div className="details">
            <Rating average={avgRating} count={reviewsCount} />
            {subscribable.plus ? <PlusLogo className="is-subscribable" /> : false}
          </div>
        </div>
      </a>
    </div>
  )
}

Hit.propTypes = {
  isRoot: PropTypes.bool,
  hit: PropTypes.shape({
    objectID: PropTypes.string,
    name: PropTypes.string,
    avg_rating: PropTypes.number,
    reviews_count: PropTypes.number,
    slug: PropTypes.string,
    subscribable: PropTypes.shape({
      plus: PropTypes.bool,
    }),
    on_sale: PropTypes.bool,
    producers: PropTypes.string,
    price: PropTypes.number,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        is_master: PropTypes.bool,
        cost_price: PropTypes.number,
        msrp: PropTypes.number,
        sku: PropTypes.string,
        isbn: PropTypes.string,
        upc: PropTypes.string,
        item_type: PropTypes.string,
        option_values: PropTypes.string,
        salePrice: PropTypes.number,
      }),
    ),
    image: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
    }),
    __queryID: PropTypes.string,
    __position: PropTypes.number,
  }),
}

/**
 * Renders product search results
 *
 * @param {Array<objects>} hits - Array of hit elements
 * @param {boolean} isRoot - Whether the hits are part of search results
 *
 * @returns {function} React Component
 */
const HitsComponent = ({ hits = [], isRoot = false }) => {
  // For when the content reaches its medium breakpoint
  const isMediumOrLarger = useMediaQuery({ minWidth: 1015 })

  return (
    <React.Fragment>
      <Box isFullwidth alignItems="center" justifyContent="space-between" isWrapped>
        <Box marginVertical={0.5}>
          <Stats />
        </Box>
        {isMediumOrLarger && <ClearFilters />}
      </Box>
      <CurrentRefinements />
      {hits.length ? (
        <Grid
          className="product-list detailed-product-list"
          gap={2}
          alignItem="center"
          rows="auto"
          columns={Grid.repeat(Grid.autoFill, Grid.minMax(10, Grid.fr(1)))}
        >
          {hits.map((hit) => (
            <Hit key={hit.objectID} hit={hit} isRoot={isRoot} />
          ))}
        </Grid>
      ) : (
        <ResultFallback />
      )}
    </React.Fragment>
  )
}

HitsComponent.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.object),
  isRoot: PropTypes.bool,
}

export const Hits = connectHits(HitsComponent)
